<template>
   <Head>
      <title>{{ $t("meta.cookie_policy.title") }}</title>
      <meta name="description" :content="$t('meta.privacy.content')" />
   </Head>

   <!-- content imported from org config -->
   <main class="container mx-auto max-w-lg px-4 pt-20">
      <div data-cy="cookie-container">
         <h1 class="h2 mb-8 text-brand-dark">{{ $t("privacy-and-terms.cookie_policy.heading") }}</h1>
         <p class="mb-4" data-cy="cookie-update-date">
            {{ $t("privacy-and-terms.cookie_policy.last_updated") }}:
            <time :datetime="dayjs(clientSettings.cookiesDateChanged).format()">
               {{ formattedDate }}
            </time>
         </p>
         <p v-if="companyName">
            {{ $t("privacy-and-terms.cookie_policy.text", { company_name: companyName, company_url: currentUrl }) }}
         </p>

         <!-- on our website -->
         <section class="mt-8">
            <h2 class="h3 mb-2">{{ $t("privacy-and-terms.cookie_policy.on_our_website.heading") }}</h2>
            <p>{{ $t("privacy-and-terms.cookie_policy.on_our_website.text") }}</p>

            <h3 class="h4 mb-2 mt-6">{{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.heading") }}</h3>
            <p>{{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.text") }}</p>

            <h4 class="h4 mb-2 mt-6">
               {{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.tech_data.heading") }}
            </h4>
            <p>{{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.tech_data.text") }}</p>

            <h4 class="h4 mt-6">
               {{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.usage_data.heading") }}
            </h4>
            <p>{{ $t("privacy-and-terms.cookie_policy.on_our_website.collection.usage_data.text") }}</p>
         </section>

         <!-- personal data collected -->
         <section class="mt-10">
            <h2 class="h3 mb-2">{{ $t("privacy-and-terms.cookie_policy.personal_data_collected.heading") }}</h2>
            <p>{{ $t("privacy-and-terms.cookie_policy.personal_data_collected.text") }}</p>

            <h3 class="h4 mb-2 mt-6">
               {{ $t("privacy-and-terms.cookie_policy.personal_data_collected.automated_technologies.heading") }}
            </h3>
            <p>{{ $t("privacy-and-terms.cookie_policy.personal_data_collected.automated_technologies.text") }}</p>
         </section>

         <!-- what are cookies -->
         <div class="mt-10">
            <h2 class="h3 mb-2">{{ $t("privacy-and-terms.cookie_policy.what_are_cookies.heading") }}</h2>
            <p>
               {{ $t("privacy-and-terms.cookie_policy.what_are_cookies.text") }}
            </p>

            <h2 class="h3 mb-2 mt-6">{{ $t("privacy-and-terms.cookie_policy.why_use_cookies.heading") }}</h2>
            <p>
               {{ $t("privacy-and-terms.cookie_policy.why_use_cookies.text") }}
            </p>

            <h2 class="h3 mb-2 mt-6">{{ $t("privacy-and-terms.cookie_policy.tracking_technologies.heading") }}</h2>
            <p>
               {{ $t("privacy-and-terms.cookie_policy.tracking_technologies.text") }}
            </p>
         </div>

         <!-- cookie types used on the site + customise button -->
         <CookiesRevisit class="mt-10" />

         <!-- update frequency -->
         <section class="mt-10">
            <h2 class="h3 mb-2">{{ $t("privacy-and-terms.cookie_policy.update_frequency.heading") }}</h2>
            <p>
               {{ $t("privacy-and-terms.cookie_policy.update_frequency.text") }}
            </p>
         </section>
      </div>
   </main>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import { Head } from "@unhead/vue/components";
import { useBaseContentStore } from "@/stores/baseContentStore";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useFormattedDate } from "@/composables/useFormattedDate";

// current base url
const currentUrl = window.location.origin;

const { t: $t } = useI18n();
const clientSettings = inject(useClientSettingsKey);
const baseContentStore = useBaseContentStore();

// computed
//-----------------------------------------------
const companyName = computed(() => {
   if (!baseContentStore.content) return null;

   const orgInfo = baseContentStore.content.organisationInfo;
   if (!orgInfo) return;

   return orgInfo.name;
});

const formattedDate = computed(() => useFormattedDate(clientSettings.cookiesDateChanged));
</script>

<style scoped></style>
